import { useEffect, useRef, useState } from 'react';
import { catalogJobMatchesQuery } from '@/lib/catalog/jobSearch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
// Static (FLOYD 2026-10-26): a lazily loaded chunk is removed by the next deploy from under an open tab.
import { syncTicketBreakRowsWithSchedule } from '@/utils/syncTicketBreakRows';
import { fetchAllNewJobCatalogRows, newJobCatalogQuery } from '@/lib/catalog/newJobCatalogQuery';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, Sparkles, Search, AlertCircle, AlertTriangle, Loader2, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CustomJobModal } from './CustomJobModal';
import { useAddonSuggestionTrigger } from '@/contexts/AddonSuggestionContext';
import { JobDetailsDialog } from './JobDetailsDialog';
import { UpsellClassificationBadge } from './UpsellClassificationBadge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { resolveJobBayRules } from '@/utils/catalogBayRules';
import { getShopDayLabel, getTodayInShopTz } from '@/utils/shopTime';
import { convertDatetimeLocalToUTC } from '@/utils/timezoneMath';
import { useShopTimezone } from '@/contexts/TimezoneContext';
// DTC-H: route the parts-ETA "now" anchor through the canonical clock
// so the persisted `parts_eta` future timestamp + delay-minutes math
// are deterministic under `clock.setFixed(...)`. ETA delay magnitudes
// (partsDelayMinutes / 180 / next-day 8am) are preserved verbatim.
import { clock } from '@/utils/clock';
import { useAuth } from '@/contexts/AuthContext';
import { notifyDayboardInvalidated } from '@/utils/dayboardInvalidation';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useProvisionalOptimizer } from '@/hooks/useProvisionalOptimizer';
import { useProvisionalThreshold } from '@/hooks/useProvisionalThreshold';
import { useAutonomousOptimizer, useFeatureFlag, useExposureFlag } from '@/hooks/useFeatureFlags';
import { ResolutionOptionsModal } from '@/components/optimizer/ResolutionOptionsModal';
import { buildLaborColumnsForAutoWrite } from '@/lib/labor/autoWriteLabor';
import { buildTechPayColumnsForAutoWrite } from '@/lib/labor/autoWriteTechPay';
import { mutateTicketSchedule } from '@/utils/mutateTicketSchedule';
import { requestServerFinish } from '@/utils/serverFinishIntent';

interface AddJobDialogProps {
  isOpen: boolean;
  onClose: () => void;
  ticketId: string;
  onJobAdded?: (job: any) => void;
}

export const AddJobDialog = ({ isOpen, onClose, ticketId, onJobAdded }: AddJobDialogProps) => {
  // CORE-BP item 6. Called only AFTER a job has genuinely joined the ticket.
  const { evaluateAfterWorkAdded } = useAddonSuggestionTrigger();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { activeShop } = useAuth();
  const { shopTimezone } = useShopTimezone();
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [submenus, setSubmenus] = useState<string[]>([]);
  const [selectedSubmenu, setSelectedSubmenu] = useState<string>('');
  const [jobs, setJobs] = useState<any[]>([]);
  const [selectedJob, setSelectedJob] = useState<any | null>(null);
  const [showCustomJobModal, setShowCustomJobModal] = useState(false);
  const [allJobs, setAllJobs] = useState<any[]>([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Ref to prevent useEffect from clearing selectedJob during search selection
  const isSelectingFromSearchRef = useRef(false);
  const [isJobDetailsOpen, setIsJobDetailsOpen] = useState(false);
  const [pendingJob, setPendingJob] = useState<any | null>(null);
  const [shopId, setShopId] = useState<string>('');
  const [ticketType, setTicketType] = useState<'Waiter' | 'Drop-off'>('Drop-off');
  const [totalMinutes, setTotalMinutes] = useState(0);
  const [jobCount, setJobCount] = useState(0);
  const [upsellClassification, setUpsellClassification] = useState<'normal' | 'upsell' | 'heavy'>('normal');
  const [recommendedType, setRecommendedType] = useState<'Waiter' | 'Drop-off' | null>(null);
  const [isAddingJob, setIsAddingJob] = useState(false);
  
  // Parts ETA state
  const [partsEtaBucket, setPartsEtaBucket] = useState<'in_stock' | 'delay_minutes' | 'same_day' | 'next_day' | 'custom'>('in_stock');
  const [partsDelayMinutes, setPartsDelayMinutes] = useState<number>(0);
  const [customPartsEta, setCustomPartsEta] = useState<Date | null>(null);
  const [teardownEnabled, setTeardownEnabled] = useState(false);

  // Provisional optimizer state (Catalyst+ only)
  const { enabled: optimizerEnabled } = useAutonomousOptimizer();
  const laborEngineEnabled = useFeatureFlag('labor_engine_enabled');
  const ticketFinancialsEnabled = useExposureFlag('ui_ticket_financials');
  const techPayEnabled = useExposureFlag('ui_technician_pay');
  const { threshold: provisionalThreshold } = useProvisionalThreshold();
  const [showResolutionModal, setShowResolutionModal] = useState(false);
  const [pendingJobConfirm, setPendingJobConfirm] = useState<{ minutes: number; notes: string } | null>(null);
  const [currentTicketMinutes, setCurrentTicketMinutes] = useState(0);
  
  const {
    isLoading: isCheckingScope,
    result: scopeResult,
    options: resolutionOptions,
    selectedOption,
    checkScopeExpansion,
    selectOption,
    applySelectedOption,
    reset: resetOptimizer,
  } = useProvisionalOptimizer();

  // Get the scope expansion issue if detected
  const scopeIssue = scopeResult?.issuesDetected.find(
    issue => issue.type === 'SCOPE_EXPANDED'
  ) ?? null;

  useEffect(() => {
    const loadData = async () => {
      setIsLoadingJobs(true);
      
      const { data } = await fetchAllNewJobCatalogRows<{ category_key: string }>('category_key', ['category_key']);
      
      if (data) {
        const uniqueCategories = [...new Set(data.map(item => item.category_key))];
        setCategories(uniqueCategories);
      }

      // Get shop_id
      const { data: shopProfile } = await supabase
        .from('shop_profile')
        .select('shop_id')
        .limit(1)
        .single();

      if (shopProfile?.shop_id) {
        setShopId(shopProfile.shop_id);
      }

      // Load all jobs for search (catalog + custom)
      const { data: catalogData } = await fetchAllNewJobCatalogRows('*', ['label']);

      let customData: any[] = [];
      if (shopProfile?.shop_id) {
        const { data: shopCustomJobs } = await supabase
          .from('jobs_custom')
          .select('*')
          .eq('shop_id', shopProfile.shop_id)
          .order('label');
        customData = shopCustomJobs || [];
      }
      
      setAllJobs([...(catalogData || []), ...customData]);
      setIsLoadingJobs(false);
    };
    
    // Load current ticket predicted_minutes for scope expansion detection
    const loadTicketMinutes = async () => {
      if (!ticketId) return;
      
      const { data: ticketData } = await supabase
        .from('tickets')
        .select('predicted_minutes, assigned_tech_id')
        .eq('id', ticketId)
        .single();
      
      if (ticketData) {
        setCurrentTicketMinutes(ticketData.predicted_minutes || 0);
      }
    };
    
    if (isOpen) {
      loadData();
      loadTicketMinutes();
      resetOptimizer();
    }
  }, [isOpen, ticketId, resetOptimizer]);

  useEffect(() => {
    const fetchSubmenus = async () => {
      // Skip fetching if job was selected from search (already have full job object)
      if (isSelectingFromSearchRef.current) {
        return;
      }
      
      if (!selectedCategory) {
        setSubmenus([]);
        setSelectedSubmenu('');
        setJobs([]);
        setSelectedJob(null);
        return;
      }

      const { data } = await newJobCatalogQuery('sub_menu')
        .eq('category_key', selectedCategory)
        .order('sub_menu');
      
      if (data) {
        const uniqueSubmenus = [...new Set(data.map(item => item.sub_menu))];
        setSubmenus(uniqueSubmenus);
      }
    };
    fetchSubmenus();
  }, [selectedCategory]);

  useEffect(() => {
    const fetchJobs = async () => {
      // Skip fetching if job was selected from search (already have full job object)
      if (isSelectingFromSearchRef.current) {
        // Reset the flag after skipping - using setTimeout to ensure state has settled
        setTimeout(() => {
          isSelectingFromSearchRef.current = false;
        }, 100);
        return;
      }
      
      if (!selectedCategory || !selectedSubmenu) {
        setJobs([]);
        setSelectedJob(null);
        return;
      }

      const { data: catalogJobs } = await newJobCatalogQuery('*')
        .eq('category_key', selectedCategory)
        .eq('sub_menu', selectedSubmenu)
        .order('label');

      const { data: shopProfile } = await supabase
        .from('shop_profile')
        .select('shop_id')
        .limit(1)
        .single();

      let customJobs: any[] = [];
      
      if (shopProfile?.shop_id) {
        const { data } = await supabase
          .from('jobs_custom')
          .select('*')
          .eq('shop_id', shopProfile.shop_id)
          .eq('category_key', selectedCategory)
          .eq('sub_menu', selectedSubmenu)
          .order('label');
        
        customJobs = data || [];
      }
      
      setJobs([...(catalogJobs || []), ...customJobs]);
    };
    fetchJobs();
  }, [selectedCategory, selectedSubmenu]);

  const handleAddJob = () => {
    if (!selectedJob) return;
    setPendingJob(selectedJob);
    setIsJobDetailsOpen(true);
  };

  const handleJobDetailsConfirm = async (minutes: number, notes: string) => {
    if (!pendingJob) return;
    
    // Check if we should trigger provisional mode (Catalyst+ only)
    const addedMinutes = minutes;
    
    // Fetch current ticket data to check if assigned
    const { data: ticketData } = await supabase
      .from('tickets')
      .select('assigned_tech_id, predicted_minutes')
      .eq('id', ticketId)
      .single();
    
    const isAssigned = !!ticketData?.assigned_tech_id;
    const shouldCheckProvisional = optimizerEnabled && isAssigned && addedMinutes > provisionalThreshold;
    
    if (shouldCheckProvisional) {
      // Store pending confirm and check for scope expansion
      setPendingJobConfirm({ minutes, notes });
      
      const result = await checkScopeExpansion(ticketId, addedMinutes);
      
      if (result?.issuesDetected.some(i => i.type === 'SCOPE_EXPANDED') && 
          result.resolutionOptions.length > 0) {
        // Show resolution modal
        setShowResolutionModal(true);
        return;
      }
    }
    
    // No provisional check needed or no issues, proceed with job add
    await executeJobAdd(minutes, notes);
  };

  const handleResolutionApply = async () => {
    const success = await applySelectedOption();
    if (success && pendingJobConfirm) {
      setShowResolutionModal(false);
      await executeJobAdd(pendingJobConfirm.minutes, pendingJobConfirm.notes);
    }
    return success;
  };

  const handleResolutionSkip = async () => {
    setShowResolutionModal(false);
    if (pendingJobConfirm) {
      await executeJobAdd(pendingJobConfirm.minutes, pendingJobConfirm.notes);
    }
  };

  const handleResolutionModalClose = (open: boolean) => {
    if (!open) {
      resetOptimizer();
      setPendingJobConfirm(null);
    }
    setShowResolutionModal(open);
  };

  const executeJobAdd = async (minutes: number, notes: string) => {
    if (!pendingJob) return;
    
    setIsAddingJob(true);
    
    try {

    // First, get the existing ticket to find the vehicle_id, assigned tech, and existing jobs
    const { data: ticketData, error: ticketError } = await supabase
      .from('tickets')
      .select('vehicle_id, predicted_minutes, assigned_tech_id, start_time, customer_name, earliest_parts_eta, has_teardown_jobs')
      .eq('id', ticketId)
      .single();

    if (ticketError) {
      toast({
        title: 'Error',
        description: 'Failed to load ticket data',
        variant: 'destructive',
      });
      return;
    }

    // Check for existing jobs - we no longer need to check for paused timers
    // since timers stay on their original jobs

    // Create a job entry with proper skill_level mapping
    // Using 10-tier system to match technician skill tiers
    const skillLevelMap: Record<string, number> = {
      'basic': 2,         // Tier 2 (Novice Plus)
      'intermediate': 4,  // Tier 4 (Proficient)
      'advanced': 7,      // Tier 7 (Advanced Plus)
      'expert': 9         // Tier 9 (Expert Plus)
    };

    const { data: shopProfile } = await supabase
      .from('shop_profile')
      .select('shop_id')
      .limit(1)
      .single();
    
    if (!shopProfile) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Shop profile not found',
      });
      return;
    }

    // Calculate skill level with diagnostics boost
    let skillLevel = skillLevelMap[pendingJob.skill_level?.toLowerCase()] || 4;
    
    // Boost diagnostics jobs to expert tier (9)
    if (pendingJob.category_key === 'diagnostics' && skillLevel === 7) {
      skillLevel = 9;
    }

    const { data: jobData, error: jobError } = await supabase
      .from('jobs')
      .insert({
        name: pendingJob.label,
        submenu: pendingJob.sub_menu,
        category: pendingJob.category_key,
        bay_requirement: 'Lift',
        skill_level: skillLevel,
        shop_id: shopProfile.shop_id,
        standard_minutes: pendingJob.minutes_default,
      })
      .select()
      .single();

    if (jobError) {
      console.error('Job creation error:', jobError);
      toast({
        title: 'Error',
        description: `Failed to create job: ${jobError.message}`,
        variant: 'destructive',
      });
      return;
    }

    // Get current user for tracking
    const { data: { user } } = await supabase.auth.getUser();

    // Calculate parts ETA based on bucket selection
    let computedPartsEta: Date | null = null;
    let computedDelayMinutes = 0;
    
    if (partsEtaBucket === 'delay_minutes') {
      computedDelayMinutes = partsDelayMinutes;
      computedPartsEta = new Date(clock.nowMs() + partsDelayMinutes * 60000);
    } else if (partsEtaBucket === 'same_day') {
      computedDelayMinutes = 180; // 3 hours default
      computedPartsEta = new Date(clock.nowMs() + 180 * 60000);
    } else if (partsEtaBucket === 'next_day') {
      const tomorrow = clock.now();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(8, 0, 0, 0);
      computedPartsEta = tomorrow;
      computedDelayMinutes = Math.floor((tomorrow.getTime() - clock.nowMs()) / 60000);
    } else if (partsEtaBucket === 'custom' && customPartsEta) {
      computedPartsEta = customPartsEta;
      computedDelayMinutes = Math.floor((customPartsEta.getTime() - clock.nowMs()) / 60000);
    }

    // Fetch bay rules from catalog and merge with shop overrides
    const { data: catalogJob } = await supabase
      .from('jobs_catalog')
      .select('id, primary_bay_type, allowed_bay_types, required_capabilities, excluded_bay_types')
      .eq('id', pendingJob.id)
      .single();

    const { data: override } = await supabase
      .from('jobs_catalog_overrides')
      .select('primary_bay_type_override, allowed_bay_types_override, required_capabilities_override, excluded_bay_types_override, disabled')
      .eq('shop_id', shopId)
      .eq('job_id', pendingJob.id)
      .maybeSingle();

    // Resolve final bay rules (global catalog + shop override)
    const resolvedRules = catalogJob 
      ? resolveJobBayRules(catalogJob, override)
      : { 
          allowed_bay_types: [], 
          required_capabilities: [], 
          excluded_bay_types: [],
          bay_requirement_source: 'catalog' as const
        };

    // Link the job to the ticket with custom minutes, notes, parts ETA, teardown, and bay snapshots
    // Phase 10 R2: also include sell-side labor columns when financial flags are ON.
    const laborCols = await buildLaborColumnsForAutoWrite({
      shopId: shopProfile.shop_id,
      effectiveMinutes: minutes,
      jobCategory: pendingJob.category_key,
      flags: {
        labor_engine_enabled: laborEngineEnabled,
        ui_ticket_financials: ticketFinancialsEnabled,
      },
    });
    // Phase 10 R3: also include tech-pay columns when pay flags are ON and a tech is assigned.
    const techPayCols = await buildTechPayColumnsForAutoWrite({
      shopId: shopProfile.shop_id,
      technicianId: ticketData.assigned_tech_id ?? null,
      referenceMinutes: minutes,
      retailSoldMinutes: (laborCols as any)?.sold_minutes ?? null,
      flags: {
        labor_engine_enabled: laborEngineEnabled,
        ui_technician_pay: techPayEnabled,
      },
    });
    const { error: linkError } = await supabase
      .from('ticket_jobs')
      .insert({
        ticket_id: ticketId,
        job_id: jobData.id,
        minutes_override: minutes !== pendingJob.minutes_default ? minutes : null,
        notes: notes || null,
        created_by_user_id: user?.id,
        parts_eta_bucket: partsEtaBucket,
        parts_delay_minutes: computedDelayMinutes,
        parts_eta: computedPartsEta?.toISOString() || null,
        teardown_enabled: teardownEnabled,
        allowed_bay_types_snapshot: resolvedRules.allowed_bay_types,
        required_capabilities_snapshot: resolvedRules.required_capabilities,
        excluded_bay_types_snapshot: resolvedRules.excluded_bay_types,
        bay_requirement_source: resolvedRules.bay_requirement_source,
        ...(laborCols ?? {}),
        ...(techPayCols ?? {}),
      });

    if (linkError) {
      console.error('Link error:', linkError);
      toast({
        title: 'Error',
        description: `Failed to link job to ticket: ${linkError.message}`,
        variant: 'destructive',
      });
      return;
    }

    // Recalculate predicted_minutes from scratch by summing ALL ticket_jobs
    // This prevents accumulation bugs from race conditions or stale data
    const { data: allTicketJobs } = await supabase
      .from('ticket_jobs')
      .select('minutes_override, teardown_enabled, jobs:job_id(standard_minutes)')
      .eq('ticket_id', ticketId);
    
    let totalPredictedMinutes = 0;
    for (const tj of allTicketJobs || []) {
      // Use minutes_override if set, otherwise use job's standard_minutes
      const jobMinutes = tj.minutes_override ?? (tj.jobs as any)?.standard_minutes ?? 0;
      totalPredictedMinutes += jobMinutes;
    }
    
    const hasTeardownJobs = allTicketJobs?.some(tj => tj.teardown_enabled) || false;

    // ROLLOVER CHAIN: a chain row's book minutes are a SHARE of the chain's
    // job ledger, never "every job on this ticket". Writing this ticket's job
    // sum into it (the root's total onto a continuation, or a continuation's
    // copies on top of the chain) double-counts. On a chain row nothing is
    // written here; the chain authority derives every share from the ledger
    // after the job line lands (recomputeRolloverChainWork below).
    const { isRolloverChainRow, recomputeRolloverChainWork } = await import('@/utils/rolloverChain');
    const isChainRow = await isRolloverChainRow(ticketId);

    // FIX-6 (Round 48): Removed client-side opti_finish computation.
    // Cascade (triggered downstream) is the authoritative source for opti_finish.
    //
    // V-A-8: routed through mutateTicketSchedule. predicted_minutes is the
    // schedule field; has_teardown_jobs / earliest_parts_eta are companion
    // metadata that must land atomically with the schedule write. The helper
    // writes last_edited_by_user_id / last_edited_at automatically when
    // userId is supplied. skipCascade:true preserves the bespoke chain
    // propagation + scoped triggerBoardCascade below — those remain the
    // authoritative cascade path for this flow.
    const mutateResult = await mutateTicketSchedule(
      ticketId,
      isChainRow ? {} : { predicted_minutes: totalPredictedMinutes },
      {
        reason: 'add_job_dialog_catalog',
        skipCascade: true,
        userId: user?.id,
        companionFields: {
          has_teardown_jobs: hasTeardownJobs,
          earliest_parts_eta: computedPartsEta?.toISOString() || ticketData.earliest_parts_eta,
        },
      }
    );

    if (!mutateResult.success) {
      console.error('[AddJobDialog] Failed to update ticket:', mutateResult.error);
      toast({ title: 'Failed to update ticket', description: mutateResult.error, variant: 'destructive' });
      return;
    }

    // Log pensieve + OCIS event for job addition via centralized helper
    const { logJobAdded, logJobNoteToOcis } = await import('@/utils/ticketEventLogger');
    await logJobAdded({
      ticketId,
      shopId,
      userId: user?.id || '',
      jobId: jobData.id,
      jobName: pendingJob.label,
      minutes: minutes || pendingJob.minutes_default,
    });
    
    // Log job note to OCIS if notes were provided
    if (notes && notes.trim()) {
      await logJobNoteToOcis({
        ticketId,
        shopId,
        userId: user?.id || '',
        jobId: jobData.id,
        jobName: pendingJob.label,
        noteContent: notes,
        isUpdate: false,
      });
    }
    
    // Invalidate ticket queries so TicketCard receives updated opti_finish and break calculations
    queryClient.invalidateQueries({ queryKey: ['tickets'] });
    queryClient.invalidateQueries({ queryKey: ['parking-lot-tickets'] });
    queryClient.invalidateQueries({ queryKey: ['ticket-history', ticketId] });
    queryClient.invalidateQueries({ queryKey: ['ticket-jobs'] });

    // The chain's new total is re-planned once, from the job ledger.
    if (isChainRow) {
      await recomputeRolloverChainWork(ticketId);
    }

    // Check for EOD violation after adding job (a chain's days never pass their dated close)
    if (!isChainRow && ticketData.assigned_tech_id && ticketData.start_time) {
      const { checkEODViolation } = await import('@/utils/eodChecker');
      await checkEODViolation(
        ticketId,
        totalPredictedMinutes,
        ticketData.start_time,
        ticketData.assigned_tech_id,
        ticketData.customer_name || 'Unknown Customer'
      );
    }

    // Trigger schedule recalculation and sync break rows if ticket is assigned to a technician
    if (ticketData.assigned_tech_id && ticketData.start_time) {
      try {
        // Resolve timezone for date derivation
        const { data: shopTzData } = await supabase.from('shop_profile').select('time_zone').eq('shop_id', shopId).maybeSingle();
        const resolvedTimezone = shopTzData?.time_zone || 'America/Los_Angeles';
        // R7-M-1: Use canonical scheduled_date; fallback via shop TZ
        const dateStr = (ticketData as any).scheduled_date ?? getShopDayLabel(ticketData.start_time, resolvedTimezone);
        
        // Sync break rows to ensure exactly one ticket owns each break (doesn't move ticket times)
        await syncTicketBreakRowsWithSchedule({
          techId: ticketData.assigned_tech_id,
          dateStr,
        });

} catch (error) {
        console.error('Error recalculating schedule:', error);
      }
    }

    // R39 FIX-12: Trigger global cascade for ALL tickets (including unassigned) after job addition
    try {
      const { triggerBoardCascade } = await import('@/utils/cascadeTrigger');
      const dateStr = (ticketData as any).scheduled_date
        ?? (ticketData.start_time
              ? getShopDayLabel(ticketData.start_time, shopTimezone)
              // Chronomancer R-PAST-15: canonical clock-aware helper.
              : getTodayInShopTz(shopTimezone));
      await triggerBoardCascade(shopId, dateStr, ticketData.assigned_tech_id || undefined);
    } catch (cascadeErr) {
      console.error('[AddJobDialog] Error triggering board cascade:', cascadeErr);
      toast({ title: '⚠️ Schedule may need refresh', description: 'Automatic recalculation failed.', skipPersist: true });
    }

    // Optimistic update callback
    const newJob = {
      id: jobData.id,
      name: pendingJob.label,
      category: pendingJob.category_key,
      submenu: pendingJob.sub_menu,
      standardMinutes: minutes || pendingJob.minutes_default,
      bayRequirement: 'Lift',
      skillLevel: skillLevelMap[pendingJob.skill_level?.toLowerCase()] || 2,
      notes: notes || undefined,
    };
    onJobAdded?.(newJob);

    // CORE-BP item 6 — the job is on the ticket; ask about the ticket's NOW
    // COMPLETE work, not about the row just added. Awaited so the question
    // appears with the add, never awaited by the insert itself.
    await evaluateAfterWorkAdded(ticketId);

    // Cross-device Dayboard sync — added job changes ticket
    // predicted_minutes/opti_finish, jobs list, and bay snapshots.
    void notifyDayboardInvalidated({
      reason: 'job-add',
      shopId: activeShop?.id || '',
      ticketId,
    });

    toast({
      title: 'Job Added',
      description: `${pendingJob.label} added to ticket`,
    });

    // Reset and close
    setSelectedCategory('');
    setSelectedSubmenu('');
    setSelectedJob(null);
    setPendingJob(null);
    setPartsEtaBucket('in_stock');
    setPartsDelayMinutes(0);
    setCustomPartsEta(null);
    setTeardownEnabled(false);
    setIsJobDetailsOpen(false);
    onClose();
    } catch (error) {
      console.error('Error adding job:', error);
    } finally {
      setIsAddingJob(false);
    }
  };

  const formatCategoryDisplay = (key: string): string => {
    return key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .trim();
  };

  return (
    <>
    <Dialog open={isOpen && !showCustomJobModal} onOpenChange={onClose}>
      <DialogContent ref={dialogContentRef} className="sm:max-w-[500px] max-h-[90dvh] overflow-y-auto" data-testid="add-job-dialog">
        <DialogHeader>
          <DialogTitle>Add Job to Ticket</DialogTitle>
          <DialogDescription>
            Select a job to add to ticket {ticketId}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Quick Search */}
          <div className="space-y-2">
            <Label>Quick Search</Label>
            <Popover open={searchOpen} onOpenChange={setSearchOpen} modal={false}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  className={cn("w-full justify-between", selectedJob && "text-left")}
                >
                  {selectedJob ? (
                    <div className="flex items-center gap-2 truncate">
                      <Check className="h-4 w-4 text-green-500 shrink-0" />
                      <span className="truncate font-medium">{selectedJob.label}</span>
                      <span className="text-xs text-muted-foreground">({selectedJob.minutes_default} min)</span>
                    </div>
                  ) : (
                    "Search jobs..."
                  )}
                  <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent container={dialogContentRef.current} className="w-[min(450px,calc(100vw-2rem))] p-0" align="start" data-testid="add-job-catalog-popover">
                {/* The shared catalog search decides (aliases included); cmdk must not re-filter by label. */}
                <Command shouldFilter={false}>
                  <CommandInput 
                    autoFocus
                    placeholder="Search jobs..." 
                    value={searchQuery}
                    onValueChange={setSearchQuery}
                  />
                  <CommandList>
                    <CommandEmpty>
                      {isLoadingJobs ? "Loading jobs..." : searchQuery.length === 0 ? "Type to search..." : "No jobs found."}
                    </CommandEmpty>
                    <CommandGroup>
                      {allJobs
                        .filter((job) => catalogJobMatchesQuery(job, searchQuery))
                        .slice(0, 15)
                        .map((job) => (
                          <CommandItem
                            key={job.id}
                            value={`${job.label} ${job.category_key} ${job.sub_menu}`}
                          onSelect={() => {
                              // Set ref flag to prevent cascading fetches (using ref avoids re-render race)
                              isSelectingFromSearchRef.current = true;
                              setSelectedJob(job);
                              setSelectedCategory(job.category_key);
                              setSelectedSubmenu(job.sub_menu);
                              setSearchQuery('');
                              setSearchOpen(false);
                            }}
                          >
                            <div className="flex flex-col">
                              <span className="font-medium">{job.label}</span>
                              <span className="text-xs text-muted-foreground">
                                {formatCategoryDisplay(job.category_key)} → {job.sub_menu} ({job.minutes_default} min)
                              </span>
                            </div>
                          </CommandItem>
                        ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Selected Job Confirmation Banner */}
          {selectedJob && (
            <div className="flex items-center justify-between p-3 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-green-100 dark:bg-green-900">
                  <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                </div>
                <div className="flex flex-col">
                  <span className="font-semibold text-green-800 dark:text-green-200">
                    {selectedJob.label}
                  </span>
                  <span className="text-xs text-green-600 dark:text-green-400">
                    {formatCategoryDisplay(selectedJob.category_key)} → {selectedJob.sub_menu} • {selectedJob.minutes_default} min
                  </span>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  setSelectedJob(null);
                  setSelectedCategory('');
                  setSelectedSubmenu('');
                }}
                className="text-green-700 hover:text-green-900 hover:bg-green-100 dark:text-green-400 dark:hover:text-green-200 dark:hover:bg-green-900"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">Or browse by category</span>
            </div>
          </div>

          {/* Category / Submenu / Job Dropdowns */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {formatCategoryDisplay(cat)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Submenu</Label>
              <Select 
                value={selectedSubmenu} 
                onValueChange={setSelectedSubmenu}
                disabled={!selectedCategory}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {submenus.map((sub) => (
                    <SelectItem key={sub} value={sub}>
                      {sub}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Job</Label>
              <Select 
                value={selectedJob?.id || ''} 
                onValueChange={(value) => {
                  const job = jobs.find(j => j.id === value);
                  setSelectedJob(job || null);
                }}
                disabled={!selectedSubmenu || jobs.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {jobs.map((job: any) => (
                    <SelectItem key={job.id} value={job.id}>
                      {job.label} ({job.minutes_default} min)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Parts ETA & Teardown Section */}
          {selectedJob && (
            <div className="border rounded-lg p-4 space-y-4 bg-muted/50">
              <h3 className="font-semibold text-sm">Parts & Teardown Options</h3>
              
              {/* Parts ETA Selector */}
              <div className="space-y-2">
                <Label>Parts Availability</Label>
                <Select value={partsEtaBucket} onValueChange={(val: any) => setPartsEtaBucket(val)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_stock">In Stock (Ready Now)</SelectItem>
                    <SelectItem value="delay_minutes">Delay (Minutes)</SelectItem>
                    <SelectItem value="same_day">Same Day (Later Today)</SelectItem>
                    <SelectItem value="next_day">Next Business Day</SelectItem>
                    <SelectItem value="custom">Custom Date/Time</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Delay Minutes Input */}
                {partsEtaBucket === 'delay_minutes' && (
                  <div className="space-y-2">
                    <Label className="text-xs">Delay (minutes)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={partsDelayMinutes}
                      onChange={(e) => setPartsDelayMinutes(parseInt(e.target.value) || 0)}
                      placeholder="Enter delay in minutes"
                    />
                  </div>
                )}
                
                {/* Custom Date Picker */}
                {partsEtaBucket === 'custom' && (
                  <div className="space-y-2">
                    <Label className="text-xs">Custom ETA Date/Time</Label>
                    <Input
                      type="datetime-local"
                      onChange={(e) => {
                        if (!e.target.value) { setCustomPartsEta(null); return; }
                        const utcIso = convertDatetimeLocalToUTC(e.target.value, shopTimezone);
                        setCustomPartsEta(new Date(utcIso));
                      }}
                    />
                  </div>
                )}
              </div>
              
              {/* Teardown Toggle */}
              {(partsEtaBucket === 'delay_minutes' || partsEtaBucket === 'same_day' || partsEtaBucket === 'next_day' || partsEtaBucket === 'custom') && (
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="teardown-enabled"
                    checked={teardownEnabled}
                    onChange={(e) => setTeardownEnabled(e.target.checked)}
                    className="h-4 w-4 rounded border-input"
                  />
                  <Label htmlFor="teardown-enabled" className="text-sm cursor-pointer">
                    Teardown required before parts arrive
                  </Label>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-between gap-2">
          <Button 
            variant="outline" 
            onClick={() => setShowCustomJobModal(true)}
          >
            <Sparkles className="mr-2 h-4 w-4" />
            Create Custom Job
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleAddJob} disabled={!selectedJob}>
              <Plus className="mr-2 h-4 w-4" />
              Add Job
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    <CustomJobModal 
      isOpen={showCustomJobModal}
      onClose={() => setShowCustomJobModal(false)}
      shopId={shopId}
      onJobCreated={async (customJob) => {
        // Create the job directly from the custom job with proper bay requirements
        const { data: shopProfile } = await supabase
          .from('shop_profile')
          .select('shop_id')
          .limit(1)
          .single();
        
        if (!shopProfile) {
          toast({
            variant: 'destructive',
            title: 'Error',
            description: 'Shop profile not found',
          });
          return;
        }

        const { data: jobData, error: jobError } = await supabase
          .from('jobs')
          .insert({
            name: customJob.name,
            submenu: customJob.submenu,
            category: customJob.category,
            bay_requirement: customJob.bay, // Now properly derived from category
            skill_level: customJob.skill,
            standard_minutes: customJob.minutes,
            shop_id: shopProfile.shop_id,
          })
          .select()
          .single();

        if (jobError) {
          toast({
            title: 'Error',
            description: 'Failed to create custom job',
            variant: 'destructive',
          });
          return;
        }

        // Get current user for tracking
        const { data: { user } } = await supabase.auth.getUser();

        // Link to ticket with bay snapshots (consumed by bayGatingValidator
        // and the move_vehicle_location RPC for bay-compatibility checks)
        await supabase
          .from('ticket_jobs')
          .insert({
            ticket_id: ticketId,
            job_id: jobData.id,
            created_by_user_id: user?.id,
            allowed_bay_types_snapshot: customJob.allowed_bay_types || [],
            required_capabilities_snapshot: customJob.required_capabilities || [],
            excluded_bay_types_snapshot: [],
            bay_requirement_source: 'category_derived',
          });

        // Recalculate predicted minutes from scratch (not carry-forward)
        const { data: allTicketJobs } = await supabase
          .from('ticket_jobs')
          .select('minutes_override, jobs:job_id(standard_minutes)')
          .eq('ticket_id', ticketId);
        
        let totalPredictedMinutes = 0;
        for (const tj of allTicketJobs || []) {
          const jobMinutes = tj.minutes_override ?? (tj.jobs as any)?.standard_minutes ?? 0;
          totalPredictedMinutes += jobMinutes;
        }

        // The finish of the custom job path (parity with the standard path — Round 37, FIX 4) is
        // the SERVER's (Core-100 R4, CORE-AC): after the new minutes land, 'place_from_start'
        // re-derives the finish from the ticket's start, technician and breaks.

        // ROLLOVER CHAIN: a chain row's minutes and finish are the chain
        // authority's, derived from the chain's job ledger — this ticket's own
        // job sum is not its share (see the catalog path). Nothing is written
        // onto a chain row here; the chain is re-planned once instead.
        const { isRolloverChainRow, recomputeRolloverChainWork } = await import('@/utils/rolloverChain');
        const isChainRow = await isRolloverChainRow(ticketId);

        // V-A-8: routed through mutateTicketSchedule. predicted_minutes
        // (and conditional opti_finish) are schedule fields. The helper
        // writes last_edited_* automatically when userId is supplied.
        // skipCascade:true preserves the triggerBoardCascade below.
        if (!isChainRow) {
          const minutesWrite = await mutateTicketSchedule(
            ticketId,
            { predicted_minutes: totalPredictedMinutes },
            {
              reason: 'add_job_dialog_custom',
              skipCascade: true,
              userId: user?.id,
            }
          );
          if (minutesWrite.success) {
            // Unassigned / unscheduled tickets are refused quietly (nothing to place).
            await requestServerFinish(ticketId, 'place_from_start', { reason: 'add_job_dialog_custom' });
          }
        }

        const { data: ticketForChainCheck } = await supabase
          .from('tickets')
          .select('shop_id, scheduled_date, assigned_tech_id')
          .eq('id', ticketId)
          .single();

        if (isChainRow) {
          await recomputeRolloverChainWork(ticketId);
        }

        try {
          const { triggerBoardCascade } = await import('@/utils/cascadeTrigger');
          if (ticketForChainCheck?.shop_id) {
            await triggerBoardCascade(
              ticketForChainCheck.shop_id,
              ticketForChainCheck.scheduled_date,
              ticketForChainCheck.assigned_tech_id || undefined
            );
          } else {
            console.warn('[AddJobDialog] ticketForChainCheck missing shop_id — cascade skipped for custom job on ticket:', ticketId);
          }
        } catch (cascadeError) {
          console.error('[AddJobDialog] Custom job cascade failed:', cascadeError);
        }

        // Optimistic update callback
        onJobAdded?.({
          id: jobData.id,
          name: customJob.name,
          category: customJob.category,
          submenu: customJob.submenu,
          standardMinutes: customJob.minutes,
          bayRequirement: customJob.bay,
          skillLevel: customJob.skill,
        });

        // CORE-BP item 6 — a custom job is still work joining this ticket, and
        // it is exactly the free-text case the rules' keyword evidence exists
        // for. Evaluated against the settled ticket like any other add.
        await evaluateAfterWorkAdded(ticketId);

        toast({
          title: 'Custom Job Added',
          description: `${customJob.name} added to ticket`,
        });

        onClose();
      }}
    />

    <JobDetailsDialog
      isOpen={isJobDetailsOpen}
      onClose={() => {
        setIsJobDetailsOpen(false);
        setPendingJob(null);
      }}
      jobName={pendingJob?.label || ''}
      defaultMinutes={pendingJob?.minutes_default || 0}
      onConfirm={handleJobDetailsConfirm}
      isLoading={isAddingJob || isCheckingScope}
    />

    {/* Resolution Options Modal for Provisional Add-On (Catalyst+ only) */}
    <ResolutionOptionsModal
      open={showResolutionModal}
      onOpenChange={handleResolutionModalClose}
      issue={scopeIssue}
      options={resolutionOptions}
      selectedOption={selectedOption}
      onSelectOption={selectOption}
      onApply={handleResolutionApply}
      onSkip={handleResolutionSkip}
      isLoading={isCheckingScope}
        promiseIssues={scopeResult?.issuesDetected}
        issuePlans={scopeResult?.issuePlans}
    />
    </>
  );
};
